<template>
  <div v-if="isRetryMode" class="checkout-extrasell-product pt-4">
    <div class="retry-payment-form">
      <ConfirmationTitle />
      <OrderRecapTitle />
      <OrderRecap :showSubtotalIfFreeShipping="true" />
      <PaymentForm
        :paymentMethodsOptions="paymentMethodsOptions"
        orderType="initialOrder"
      />
    </div>
  </div>
</template>

<script>
import PaymentForm from "@/components/checkout/PaymentForm.vue";
import ConfirmationTitle from "@/components/checkout/ConfirmationTitle.vue";
import OrderRecapTitle from "@/components/checkout/OrderRecapTitle.vue";
import OrderRecap from "@/components/checkout/OrderRecap.vue";
import { useOrderHistoryStore } from "@/store/orderHistory";
import { useDraftStore } from "@/store/draft";
import CartService from "@/services/cart";
import Utils from "@/services/utils";
import { getPaymentRetryOrderId } from "@/services/orderPageRetryParam";

export default {
  name: "OrderPageRetry",
  components: {
    PaymentForm,
    ConfirmationTitle,
    OrderRecapTitle,
    OrderRecap,
  },
  props: {
    paymentMethodsOptions: {
      type: Object,
      default: () => ({
        sepa: { cssClass: "field-icon fa-solid fa-building-columns" },
        bank_card: { cssClass: "field-icon fa-solid fa-credit-card" },
        iframeStyle: "orderPage",
      }),
    },
  },
  data() {
    return {
      retryOrderId: null,
      retryItem: null,
      isReady: false,
    };
  },
  computed: {
    isRetryMode() {
      return !!this.retryOrderId && this.isReady;
    },
  },
  mounted() {
    this.retryOrderId = getPaymentRetryOrderId(
      new URL(window.location).searchParams,
    );

    if (this.retryOrderId) {
      this.loadRetryData();
      // Only hide content if we successfully loaded retry data
      if (this.isReady) {
        this.hideNormalPageContent();
      }
    }
  },
  beforeUnmount() {
    this.showNormalPageContent();
  },
  methods: {
    loadRetryData() {
      const orderHistoryStore = useOrderHistoryStore();
      const orderHistory = orderHistoryStore.currentPageOrderHistory;

      if (orderHistory && orderHistory.length > 0) {
        // Find the order matching the retry ID (using order.externalOrderId)
        this.retryItem = orderHistory.find(
          (item) => item.order?.externalOrderId === this.retryOrderId,
        );

        if (this.retryItem) {
          // Rebuild cart from previous order
          this.rebuildCartFromRetry();

          // Create new retry order with new ID
          const newOrder = {
            ...this.retryItem.order,
            externalOrderId: Utils.generateUID(),
            status: "pending",
            isRetry: true,
          };
          useDraftStore().updateOrder(newOrder);

          // Mark form as valid for retry
          useDraftStore().formValid(true);

          // Mark as ready to render
          this.isReady = true;
        }
      }
    },
    rebuildCartFromRetry() {
      // Clear existing cart first
      CartService.emptyCart();

      // Reconstruct cart from previous order history
      for (const oldCartItem of this.retryItem.cart.items) {
        const newCartItem = { ...oldCartItem };
        newCartItem.qtyIncr = 1;

        // Ensure choice.name exists (may be missing in older orderHistory entries)
        if (newCartItem.choice && !newCartItem.choice.name) {
          newCartItem.choice.name = newCartItem.name || "Product";
        }

        CartService.addToCart(newCartItem);
      }
    },
    hideNormalPageContent() {
      // Hide the entire row containing both columns
      const mainRow = document.querySelector(".row.row-1");
      if (mainRow) {
        mainRow.dataset.hiddenByRetry = "true";
        mainRow.style.display = "none";

        // Remove duplicate iframes from the hidden section to prevent conflicts
        // Keep only our retry UI's iframe
        mainRow.querySelectorAll("iframe").forEach((iframe) => {
          iframe.dataset.removedByRetry = "true";
          iframe.src = "about:blank";
          iframe.remove();
        });

        // Also remove any other payment forms to prevent duplicate message handlers
        mainRow.querySelectorAll(".payment-form").forEach((form) => {
          form.dataset.hiddenByRetry = "true";
          form.innerHTML = "";
        });
      }

      // Move our retry UI to the main container, outside the hidden row
      const container = document.querySelector(".container");
      if (container && this.$el) {
        // Create a wrapper div
        const wrapper = document.createElement("div");
        wrapper.className = "retry-ui-wrapper";
        wrapper.dataset.createdByRetry = "true";
        wrapper.style.cssText =
          "max-width: 600px; margin: 40px auto; padding: 20px;";

        // Move the actual element (not clone) to preserve Vue reactivity
        wrapper.appendChild(this.$el);

        // Insert wrapper at the beginning of container
        container.insertBefore(wrapper, container.firstChild);
      }
    },
    showNormalPageContent() {
      // Remove the wrapper we created
      document
        .querySelectorAll('[data-created-by-retry="true"]')
        .forEach((el) => {
          el.remove();
        });

      // Restore all hidden elements
      document
        .querySelectorAll('[data-hidden-by-retry="true"]')
        .forEach((el) => {
          el.style.display = "";
          delete el.dataset.hiddenByRetry;
        });
    },
  },
};
</script>
